import React from 'react';
import { Relationship } from '../types';
import { AlertCircle, CreditCard, ChevronRight } from 'lucide-react';
import Avatar from './Avatar';
import { useTranslation } from '../lib/translations';
import Tooltip from './Tooltip';

interface StudentCardProps {
  relationship: Relationship;
  onClick: () => void;
  variant?: 'grid' | 'list';
}

const StudentCard: React.FC<StudentCardProps> = ({ relationship, onClick, variant = 'grid' }) => {
  const { t } = useTranslation();
  const creditsRemaining = relationship.lessonsPaid - relationship.lessonsCompleted;
  const isOutOfCredits = creditsRemaining <= 0;
  const isPending = relationship.studentId?.startsWith('pending:');

  if (variant === 'list') {
    return (
      <button 
        onClick={onClick}
        className="w-full text-left group bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl p-4 transition-all duration-300 hover:border-purple-500/50 hover:shadow-lg dark:hover:shadow-purple-500/10 flex items-center gap-4 min-w-0"
      >
        <div className="relative shrink-0">
          <Avatar 
            src={relationship.studentPhoto} 
            name={relationship.studentName} 
            isPending={isPending}
            color={relationship.color}
            className="w-12 h-12"
          />
          {isOutOfCredits && (
            <div className="absolute -top-1 -right-1 bg-red-500 p-0.5 rounded-full shadow-lg border-2 border-white dark:border-slate-900">
              <AlertCircle size={8} className="text-white" />
            </div>
          )}
        </div>
        
        <div className="min-w-0 flex-1 flex flex-col md:flex-row md:items-center gap-2 md:gap-8">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors truncate">
              {relationship.studentName}
            </h3>
            <p className="text-[10px] font-medium text-slate-400 dark:text-slate-500 truncate">{relationship.studentEmail}</p>
          </div>

          <div className="flex items-center gap-6 shrink-0">
            <div className="flex flex-col items-end">
              <div className="flex items-center gap-1 text-[9px] text-slate-500 uppercase tracking-widest font-bold mb-0.5">
                <CreditCard size={10} className={isOutOfCredits ? 'text-red-500' : 'text-teal-500'} />
                {t.common.credits}
              </div>
              <div className="flex items-baseline gap-1">
                <span className={`text-base font-black tracking-tight ${isOutOfCredits ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-slate-100'}`}>
                  {creditsRemaining}
                </span>
                <span className="text-[8px] font-bold text-slate-400 uppercase">{t.common.left}</span>
              </div>
            </div>

            <div className="flex flex-col items-end">
              <div className="text-[9px] text-slate-500 uppercase tracking-widest font-bold mb-0.5">
                {t.common.lessons}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-base font-black text-slate-900 dark:text-slate-100 tracking-tight">
                  {relationship.lessonsCompleted}
                </span>
                <span className="text-[8px] font-bold text-slate-400 uppercase">{t.common.done}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-2 bg-slate-50 dark:bg-white/5 rounded-lg group-hover:bg-purple-600 group-hover:text-white transition-all shrink-0">
          <ChevronRight size={18} />
        </div>
      </button>
    );
  }

  return (
    <button 
      onClick={onClick}
      className="w-full text-left group bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-lg p-5 transition-all duration-300 hover:border-purple-500/50 hover:shadow-lg dark:hover:shadow-purple-500/10 flex flex-col min-w-0"
    >
      <div className="flex items-start justify-between mb-5 w-full gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="relative shrink-0">
            <Avatar 
              src={relationship.studentPhoto} 
              name={relationship.studentName} 
              isPending={isPending}
              color={relationship.color}
              className="w-12 h-12"
            />
            {isOutOfCredits && (
              <div className="absolute -top-1.5 -right-1.5 bg-red-500 p-1 rounded-full shadow-lg border-2 border-white dark:border-slate-900">
                <AlertCircle size={10} className="text-white" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <Tooltip content={relationship.studentName || ''} position="top" className="w-full">
              <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors truncate block">
                {relationship.studentName}
              </h3>
            </Tooltip>
            <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500 truncate" title={relationship.studentEmail}>{relationship.studentEmail}</p>
          </div>
        </div>
        <div className="p-1.5 bg-slate-50 dark:bg-white/5 rounded-md group-hover:bg-purple-600 group-hover:text-white transition-all shrink-0 self-center">
          <ChevronRight size={16} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 w-full">
        <div className={`rounded-md p-3 border transition-all duration-300 ${
          isOutOfCredits 
          ? 'bg-red-50 dark:bg-red-500/5 border-red-100 dark:border-red-500/20' 
          : 'bg-slate-50 dark:bg-slate-950 border-slate-100 dark:border-white/5 group-hover:border-teal-500/20'
        }`}>
          <div className="flex items-center gap-2 text-[9px] text-slate-500 mb-0.5 uppercase tracking-widest font-bold">
            <CreditCard size={10} className={isOutOfCredits ? 'text-red-500' : 'text-teal-500'} />
            {t.common.credits}
          </div>
          <div className="flex items-baseline gap-1">
            <span className={`text-xl font-black tracking-tighter ${isOutOfCredits ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-slate-100'}`}>
              {creditsRemaining}
            </span>
            <span className="text-[9px] font-bold text-slate-400 uppercase">{t.common.left}</span>
          </div>
        </div>

        <div className="bg-slate-50 dark:bg-slate-950 rounded-md p-3 border border-slate-100 dark:border-white/5 group-hover:border-purple-500/20 transition-all duration-300">
          <div className="text-[9px] text-slate-500 mb-0.5 uppercase tracking-widest font-bold">
            {t.common.lessons}
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-black text-slate-900 dark:text-slate-100 tracking-tighter">
              {relationship.lessonsCompleted}
            </span>
            <span className="text-[9px] font-bold text-slate-400 uppercase">{t.common.done}</span>
          </div>
        </div>
      </div>
      
      {isOutOfCredits && (
        <div className="mt-3 px-2 py-1 bg-red-100 dark:bg-red-500/10 rounded-md flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
          <span className="text-[9px] font-black uppercase tracking-widest text-red-600 dark:text-red-400">{t.common.paymentDue}</span>
        </div>
      )}
    </button>
  );
};

export default StudentCard;